import { Pencil, User } from 'lucide-react'
import { useState } from 'react'

import { createFileRoute, useNavigate } from '@tanstack/react-router'
import PageHeader from '@/components/settings/PageHeader'
import BecomeCreatorCard from '@/components/settings/BecomeCreatorCard'
import FormField from '@/components/settings/FormField'
import { BecomeCreatorBottomSheet } from '@/components/settings/bottom-sheets/BecomeCreator'
import { ROUTES } from '@/constants/routes'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useUserSummary, useUserProfile } from '@/hooks/api/useUsers'
import { Role } from '@wtf/shared'

export const Route = createFileRoute('/settings/account/')({
    component: RouteComponent,
})

function RouteComponent() {
    return <AccountPage />
}

const AccountPage = () => {
    const navigate = useNavigate()
    const [openBecomeCreator, setOpenBecomeCreator] = useState(false)
    const [isBecomeCreatorSubmitted, setIsBecomeCreatorSubmitted] =
        useState(false)
    const { user, loading: userLoading } = useCurrentUser()
    const { data: userSummary, isLoading: summaryLoading } = useUserSummary(
        user?.uid
    )
    const {
        data: profile,
        isLoading: profileLoading,
        error: profileError,
    } = useUserProfile(user?.uid)
    const isStylist = userSummary?.role === Role.STYLIST
    const isLoading = userLoading || summaryLoading || profileLoading

    if (profileError) {
        return (
            <div className="h-[100dvh] overflow-y-auto bg-white px-2 pb-14">
                <PageHeader title="Account" />
                <div className="flex min-h-[50vh] flex-col items-center justify-center gap-6 py-2">
                    <p className="text-red-500">
                        Error loading profile data. Please try again.
                    </p>
                </div>
            </div>
        )
    }

    return (
        <>
            <div className="h-[100dvh] overflow-y-auto bg-white px-2 pb-14">
                {/* Header */}
                <PageHeader title="Account" />

                {isLoading ? (
                    <div className="flex min-h-[50vh] flex-col items-center justify-center gap-6 py-2">
                        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-black"></div>
                        <p>Loading account data...</p>
                    </div>
                ) : (
                    <div className="flex flex-col gap-6 py-2">
                        {/* Profile Picture */}
                        <div className="flex flex-col items-center gap-4">
                            <div className="relative flex h-[111px] w-[111px] items-center justify-center overflow-hidden rounded-full bg-gray-100">
                                {profile?.avatarUrl ? (
                                    <img
                                        src={profile.avatarUrl}
                                        alt="Profile"
                                        className="h-full w-full object-cover"
                                        loading="eager"
                                    />
                                ) : (
                                    <User className="text-black" size={48} />
                                )}
                            </div>
                        </div>

                        {/* Become Creator Card */}
                        {!isLoading && !isStylist && (
                            <BecomeCreatorCard
                                onClick={() => setOpenBecomeCreator(true)}
                                isSubmitted={isBecomeCreatorSubmitted}
                            />
                        )}

                        {/* Form Fields */}
                        <div className="border-black-6 flex flex-col rounded-2xl border">
                            <FormField
                                label="Username"
                                value={profile?.username || ''}
                                readOnly
                            />
                            <FormField
                                label="Full name"
                                value={profile?.name || ''}
                                readOnly
                            />
                            <FormField
                                label="Bio"
                                value={profile?.bio || ''}
                                readOnly
                            />
                            <FormField
                                label="Links"
                                value={profile?.website || ''}
                                readOnly
                            />
                        </div>

                        <FormField
                            label="Password"
                            value="************"
                            readOnly
                            rightElement={
                                <button
                                    className="flex items-center gap-1 text-black"
                                    onClick={() =>
                                        navigate({ to: ROUTES.CHANGE_PASSWORD })
                                    }
                                >
                                    Change
                                    <Pencil className="h-4 w-4" />
                                </button>
                            }
                            className="rounded-xl border"
                        />
                    </div>
                )}
            </div>

            {!isLoading && !isStylist && (
                <BecomeCreatorBottomSheet
                    open={openBecomeCreator}
                    onClose={() => setOpenBecomeCreator(false)}
                    isSubmitted={isBecomeCreatorSubmitted}
                    setIsSubmitted={setIsBecomeCreatorSubmitted}
                />
            )}
        </>
    )
}

export default AccountPage
